import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bitcoin, Gift, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import api, { ngn } from "@/lib/api";

export default function Rates() {
  const [tab, setTab] = useState("crypto");
  const [crypto, setCrypto] = useState([]);
  const [cards, setCards] = useState([]);

  const load = () => {
    api.get("/crypto/rates").then((r) => setCrypto(r.data)).catch(() => {});
    api.get("/giftcards/rates").then((r) => setCards(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/crypto/rates").then((r) => setCrypto(r.data)).catch(() => {}), 45000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="tb-glow min-h-screen">
      <Header />
      <section className="pt-28 sm:pt-36 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold text-white">Live <span className="tb-gradient-text">Rates</span></h1>
          <p className="mt-3 text-gray-400">Real-time crypto prices (CoinGecko) and today's best gift card payouts.</p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 rounded-full bg-[#101C3F] border border-blue-500/20">
            <button data-testid="rate-crypto-tab" onClick={() => setTab("crypto")} className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === "crypto" ? "tb-btn-primary" : "text-gray-300"}`}>
              <Bitcoin size={16} /> Crypto
            </button>
            <button data-testid="rate-giftcard-tab" onClick={() => setTab("giftcards")} className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === "giftcards" ? "tb-btn-primary" : "text-gray-300"}`}>
              <Gift size={16} /> Gift Cards
            </button>
          </div>
        </div>

        {tab === "crypto" ? (
          <div className="tb-card overflow-hidden">
            <div className="hidden sm:grid grid-cols-5 gap-4 px-6 py-4 text-xs uppercase tracking-wider text-gray-500 border-b border-blue-900/30">
              <span className="col-span-2">Asset</span><span>Price (NGN)</span><span>Price (USD)</span><span className="text-right">24h</span>
            </div>
            {crypto.map((r) => (
              <div key={r.coin} data-testid={`rates-row-${r.symbol}`} className="grid grid-cols-2 sm:grid-cols-5 gap-4 px-6 py-4 items-center border-b border-blue-900/20 hover:bg-[#17244F]/50 transition-colors">
                <div className="col-span-2 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center font-mono-tb text-blue-200 text-xs font-bold">{r.symbol}</span>
                  <div><div className="font-semibold text-white text-sm">{r.name}</div><div className="text-xs text-gray-500">{r.symbol}</div></div>
                </div>
                <div className="font-mono-tb font-bold text-emerald-400 text-sm">{ngn(r.price_ngn)}</div>
                <div className="font-mono-tb text-gray-300 text-sm hidden sm:block">${r.price_usd?.toLocaleString() ?? "—"}</div>
                <div className={`text-right text-sm font-semibold ${r.change_24h >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {r.change_24h != null ? `${r.change_24h >= 0 ? "▲" : "▼"} ${Math.abs(r.change_24h)}%` : "—"}
                </div>
              </div>
            ))}
            {crypto.length === 0 && <div className="py-12 text-center text-gray-500">Loading rates…</div>}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {cards.map((c) => (
              <div key={c.id} data-testid={`giftcard-rate-${c.brand.replace(/\s|\//g, "-").toLowerCase()}`} className="tb-card p-6 hover:bg-[#17244F] transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="w-11 h-11 rounded-xl bg-blue-500/15 flex items-center justify-center text-blue-200"><Gift size={20} /></span>
                  <span className="text-xs font-semibold text-emerald-400 bg-emerald-500/10 rounded-full px-2.5 py-1">Buying</span>
                </div>
                <h3 className="font-head font-semibold text-white text-lg">{c.brand}</h3>
                <p className="text-sm text-gray-500 mb-3">{c.category} card</p>
                <div className="font-mono-tb font-bold text-emerald-400 text-xl">{ngn(c.rate_ngn)} <span className="text-sm text-gray-400 font-sans">/ {c.category === "USD" ? "$" : c.category}</span></div>
              </div>
            ))}
            {cards.length === 0 && <div className="col-span-full py-12 text-center text-gray-500">No gift card rates yet.</div>}
          </div>
        )}

        <div className="mt-10 text-center">
          <Link to="/signup" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold tb-btn-primary">Start trading at these rates <ArrowRight size={18} /></Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
